import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  InputAdornment,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  PowerSettingsNew as PowerIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Phone as PhoneIcon,
  Tag as TagIcon,
  AccessTime as TimeIcon,
  Sms as SmsIcon,
  FiberManualRecord as RecordIcon,
  PhoneAndroid as SoftphoneIcon,
} from '@mui/icons-material';
import { useAuthWithCustomerOverride } from '../../hooks/useAuthWithCustomerOverride';
import api from '../../services/api';
import activityLog from '../../services/activityLog.service';

interface Package {
  PackageID: number;
  PackageName: string;
  Description: string;
  MonthlyPrice: number;
  SetupFee: number;
  IncludedExtensions: number;
  AdditionalExtenRate: number;
  MaxExtensions: number | null;
  IncludedDIDs: number;
  AdditionalDIDRate: number;
  MaxDIDs: number | null;
  IncludedMinutes: number;
  ExtraMinuteRate: number;
  IncludesSMS: boolean;
  IncludesRecording: boolean;
  MaxRecordingStorage: number;
  RecStorageRate: number;
  Softphoneenabled: boolean;
  Softphoneincluded: boolean;
  SoftphoneRate: number;
  SoftphoneUsersCanEnable: boolean;
  SoftphoneCreateOnPWchange: boolean;
  SoftphoneCreateWithExten: boolean;
  LiveOnSite: boolean;
  Active: boolean;
  AssignedCustomers: number;
  // New fields for SMS/MMS pricing
  sms_included?: number;
  sms_rate?: number;
  mms_included?: number;
  mms_rate?: number;
  // Promotional pricing
  promo_price?: number;
  promo_end_date?: string;
  // Display options
  is_recommended?: boolean;
  display_order?: number;
  custom_features?: string;
}

const ServicePackages: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { member, isLoginAsCustomer } = useAuthWithCustomerOverride();
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<Package | null>(null);
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  
  // Form state with string | number union types for numeric fields
  const [formData, setFormData] = useState<{
    PackageName?: string;
    Description?: string;
    MonthlyPrice?: string | number;
    SetupFee?: string | number;
    IncludedExtensions?: string | number;
    AdditionalExtenRate?: string | number;
    MaxExtensions?: number | null;
    IncludedDIDs?: string | number;
    AdditionalDIDRate?: string | number;
    MaxDIDs?: number | null;
    IncludedMinutes?: string | number;
    ExtraMinuteRate?: string | number;
    IncludesSMS?: boolean;
    IncludesRecording?: boolean;
    MaxRecordingStorage?: string | number;
    RecStorageRate?: string | number;
    Softphoneenabled?: boolean;
    Softphoneincluded?: boolean;
    SoftphoneRate?: string | number;
    SoftphoneUsersCanEnable?: boolean;
    SoftphoneCreateOnPWchange?: boolean;
    SoftphoneCreateWithExten?: boolean;
    LiveOnSite?: boolean;
    // New fields
    sms_included?: string | number;
    sms_rate?: string | number;
    mms_included?: string | number;
    mms_rate?: string | number;
    promo_price?: string | number;
    promo_end_date?: string;
    is_recommended?: boolean;
    display_order?: string | number;
    custom_features?: string;
  }>({
    PackageName: '',
    Description: '',
    MonthlyPrice: '',
    SetupFee: '',
    IncludedExtensions: '',
    AdditionalExtenRate: '',
    MaxExtensions: null,
    IncludedDIDs: '',
    AdditionalDIDRate: '',
    MaxDIDs: null,
    IncludedMinutes: '',
    ExtraMinuteRate: '',
    IncludesSMS: false,
    IncludesRecording: false,
    MaxRecordingStorage: '',
    RecStorageRate: '',
    Softphoneenabled: false,
    Softphoneincluded: false,
    SoftphoneRate: '',
    SoftphoneUsersCanEnable: false,
    SoftphoneCreateOnPWchange: false,
    SoftphoneCreateWithExten: false,
    LiveOnSite: false,
    // Initialize new fields
    sms_included: '',
    sms_rate: '',
    mms_included: '',
    mms_rate: '',
    promo_price: '',
    promo_end_date: '',
    is_recommended: false,
    display_order: '',
    custom_features: '',
  });

  useEffect(() => {
    if (member?.memberType === 'master_admin') {
      fetchPackages();
    }
  }, [member]);

  // Listen for login-as-customer changes to update tab title
  useEffect(() => {
    const handleLoginAsChange = (event: CustomEvent) => {
      if (event.detail && event.detail.customerId && event.detail.customerName) {
        document.title = `${event.detail.customerId} - ${event.detail.customerName} - Service Packages`;
      }
    };

    window.addEventListener('loginAsCustomerChanged', handleLoginAsChange as EventListener);

    // Set initial title if in login-as mode
    if (isLoginAsCustomer && member) {
      const customerId = (member as any).customerID || (member as any).customer_id;
      const customerName = (member as any).loginAsCustomerName || '';
      if (customerId && customerName) {
        document.title = `${customerId} - ${customerName} - Service Packages`;
      }
    }

    return () => {
      window.removeEventListener('loginAsCustomerChanged', handleLoginAsChange as EventListener);
      // Reset title when component unmounts
      if (!isLoginAsCustomer) {
        document.title = 'Portal';
      }
    };
  }, [isLoginAsCustomer, member]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await api.get('/endpoints/packages');
      if (response.data.success && response.data.data?.packages) {
        setPackages(response.data.data.packages);
      }
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load packages');
      // Don't await logging in catch blocks to prevent blocking
      activityLog.error('fetch_packages', 'Failed to load packages', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (pkg?: Package) => {
    if (pkg) {
      setEditingPackage(pkg);
      setFormData(pkg);
    } else {
      setEditingPackage(null);
      setFormData({
        PackageName: '',
        Description: '',
        MonthlyPrice: '',
        SetupFee: '',
        IncludedExtensions: '',
        AdditionalExtenRate: '',
        MaxExtensions: null,
        IncludedDIDs: '',
        AdditionalDIDRate: '',
        MaxDIDs: null,
        IncludedMinutes: '',
        ExtraMinuteRate: '',
        IncludesSMS: false,
        IncludesRecording: false,
        MaxRecordingStorage: '',
        RecStorageRate: '',
        Softphoneenabled: false,
        Softphoneincluded: false,
        SoftphoneRate: '',
        SoftphoneUsersCanEnable: false,
        SoftphoneCreateOnPWchange: false,
        SoftphoneCreateWithExten: false,
        LiveOnSite: false,
        // Reset new fields
        sms_included: '',
        sms_rate: '',
        mms_included: '',
        mms_rate: '',
        promo_price: '',
        promo_end_date: '',
        is_recommended: false,
        display_order: '',
        custom_features: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPackage(null);
  };

  const handleSubmit = async () => {
    try {
      const endpoint = editingPackage 
        ? `/endpoints/packages/${editingPackage.PackageID}`
        : '/endpoints/packages';
      const method = editingPackage ? 'put' : 'post';
      
      // Don't await logging to prevent blocking UI
      activityLog.debug('package_submit', `Submitting package: ${method} ${endpoint}`, { 
        endpoint, 
        method, 
        editingPackage: editingPackage?.PackageID 
      });
      
      // Convert empty strings to 0, preserve user-entered values including 0
      const submitData = {
        ...formData,
        MonthlyPrice: formData.MonthlyPrice === '' ? 0 : Number(formData.MonthlyPrice),
        SetupFee: formData.SetupFee === '' ? 0 : Number(formData.SetupFee),
        IncludedExtensions: formData.IncludedExtensions === '' ? 0 : Number(formData.IncludedExtensions),
        AdditionalExtenRate: formData.AdditionalExtenRate === '' ? 0 : Number(formData.AdditionalExtenRate),
        IncludedDIDs: formData.IncludedDIDs === '' ? 0 : Number(formData.IncludedDIDs),
        AdditionalDIDRate: formData.AdditionalDIDRate === '' ? 0 : Number(formData.AdditionalDIDRate),
        IncludedMinutes: formData.IncludedMinutes === '' ? 0 : Number(formData.IncludedMinutes),
        ExtraMinuteRate: formData.ExtraMinuteRate === '' ? 0 : Number(formData.ExtraMinuteRate),
        MaxRecordingStorage: formData.MaxRecordingStorage === '' ? 0 : Number(formData.MaxRecordingStorage),
        RecStorageRate: formData.RecStorageRate === '' ? 0 : Number(formData.RecStorageRate),
        SoftphoneRate: formData.SoftphoneRate === '' ? 0 : Number(formData.SoftphoneRate),
      };
      
      activityLog.debug('package_submit_data', 'Package submit data prepared', submitData);
      
      const response = await api[method](endpoint, submitData);
      
      if (response.data.success) {
        const successMessage = editingPackage ? 'Package updated successfully' : 'Package created successfully';
        // Don't await logging to prevent blocking UI
        activityLog.success(
          editingPackage ? 'update_package' : 'add_package',
          `${successMessage}: ${formData.PackageName}`,
          { packageId: editingPackage?.PackageID || response.data.package_id }
        );
        setSuccess(successMessage);
        handleCloseDialog();
        fetchPackages();
      }
    } catch (err: any) {
      // Don't await logging to prevent blocking UI
      activityLog.error('package_submit', 'Failed to save package', err);
      setError(err.response?.data?.message || 'Failed to save package');
    }
  };

  const handleToggleStatus = async (pkg: Package) => {
    try {
      const response = await api.put(`/endpoints/packages/${pkg.PackageID}/toggle`, {
        status: pkg.Active ? 'deactivate' : 'activate'
      });
      
      if (response.data.success) {
        setSuccess(`Package ${pkg.Active ? 'deactivated' : 'activated'} successfully`);
        fetchPackages();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update package status');
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Common props for number inputs to hide spinners
  const numberInputProps = {
    style: { MozAppearance: 'textfield' as const },
    sx: {
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },
    },
  };

  if (member?.memberType !== 'master_admin') {
    return (
      <Alert severity="error">
        You do not have permission to access this section.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Service Packages</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Create New Package
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {isMobile ? (
        // Mobile Card View
        <Box>
          {packages.map((pkg) => (
            <Card key={pkg.PackageID} sx={{ mb: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="start">
                  <Box>
                    <Typography variant="h6">{pkg.PackageName}</Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {pkg.Description || 'No description'}
                    </Typography>
                  </Box>
                  <Chip
                    label={pkg.Active ? 'Active' : 'Inactive'}
                    color={pkg.Active ? 'success' : 'error'}
                    size="small"
                  />
                </Box>

                <Box mt={2}>
                  <Typography variant="h5" color="primary">
                    ${pkg.MonthlyPrice.toFixed(2)}/month
                  </Typography>
                  {pkg.SetupFee > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      + ${pkg.SetupFee.toFixed(2)} setup
                    </Typography>
                  )}
                </Box>

                {pkg.AssignedCustomers > 0 && (
                  <Chip
                    label={`${pkg.AssignedCustomers} customer${pkg.AssignedCustomers > 1 ? 's' : ''}`}
                    color="primary"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}

                <Box mt={2}>
                  <Button
                    size="small"
                    onClick={() => setExpandedCard(expandedCard === pkg.PackageID ? null : pkg.PackageID)}
                    endIcon={expandedCard === pkg.PackageID ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  >
                    View Details
                  </Button>
                </Box>

                <Collapse in={expandedCard === pkg.PackageID}>
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>Features:</Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><PhoneIcon fontSize="small" /></ListItemIcon>
                        <ListItemText 
                          primary={`${pkg.IncludedExtensions} Extensions`}
                          secondary={`+$${pkg.AdditionalExtenRate}/additional`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><TagIcon fontSize="small" /></ListItemIcon>
                        <ListItemText 
                          primary={`${pkg.IncludedDIDs} DIDs`}
                          secondary={`+$${pkg.AdditionalDIDRate}/additional`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><TimeIcon fontSize="small" /></ListItemIcon>
                        <ListItemText 
                          primary={`${pkg.IncludedMinutes} Minutes`}
                          secondary={`$${pkg.ExtraMinuteRate}/extra minute`}
                        />
                      </ListItem>
                      {pkg.IncludesSMS && (
                        <ListItem>
                          <ListItemIcon><SmsIcon fontSize="small" /></ListItemIcon>
                          <ListItemText primary="SMS Enabled" />
                        </ListItem>
                      )}
                      {pkg.IncludesRecording && (
                        <ListItem>
                          <ListItemIcon><RecordIcon fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Call Recording" />
                        </ListItem>
                      )}
                      {pkg.Softphoneenabled && (
                        <ListItem>
                          <ListItemIcon><SoftphoneIcon fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Softphone Enabled" />
                        </ListItem>
                      )}
                    </List>
                  </Box>
                </Collapse>
              </CardContent>
              <CardActions>
                <IconButton
                  color="primary"
                  onClick={() => handleOpenDialog(pkg)}
                  size="small"
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  color={pkg.Active ? 'warning' : 'success'}
                  onClick={() => handleToggleStatus(pkg)}
                  size="small"
                >
                  <PowerIcon />
                </IconButton>
              </CardActions>
            </Card>
          ))}
        </Box>
      ) : (
        // Desktop Table View
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Package Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Monthly Price</TableCell>
                <TableCell>Extensions</TableCell>
                <TableCell>DIDs</TableCell>
                <TableCell>Features</TableCell>
                <TableCell>Usage</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {packages.map((pkg) => (
                <TableRow key={pkg.PackageID}>
                  <TableCell>{pkg.PackageName}</TableCell>
                  <TableCell>{pkg.Description || 'N/A'}</TableCell>
                  <TableCell>
                    ${pkg.MonthlyPrice.toFixed(2)}
                    {pkg.SetupFee > 0 && (
                      <Chip
                        label={`+$${pkg.SetupFee.toFixed(2)} setup`}
                        size="small"
                        color="info"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {pkg.IncludedExtensions} included
                    <Typography variant="caption" display="block" color="text.secondary">
                      +${pkg.AdditionalExtenRate.toFixed(2)} per additional
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {pkg.IncludedDIDs} included
                    <Typography variant="caption" display="block" color="text.secondary">
                      +${pkg.AdditionalDIDRate.toFixed(2)} per additional
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <List dense disablePadding>
                      <ListItem sx={{ py: 0 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          {pkg.IncludedMinutes > 0 ? <CheckIcon color="success" fontSize="small" /> : <CloseIcon color="error" fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText 
                          primary={pkg.IncludedMinutes > 0 ? `${pkg.IncludedMinutes.toLocaleString()} minutes` : 'No included minutes'}
                          primaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          {pkg.IncludesSMS ? <CheckIcon color="success" fontSize="small" /> : <CloseIcon color="error" fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText 
                          primary={pkg.IncludesSMS ? 'SMS enabled' : 'No SMS'}
                          primaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          {pkg.IncludesRecording ? <CheckIcon color="success" fontSize="small" /> : <CloseIcon color="error" fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText 
                          primary={pkg.IncludesRecording ? 'Call recording' : 'No recording'}
                          primaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          {pkg.Softphoneenabled ? <CheckIcon color="success" fontSize="small" /> : <CloseIcon color="error" fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText 
                          primary={pkg.Softphoneenabled ? 'Softphone enabled' : 'No softphone'}
                          primaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    </List>
                  </TableCell>
                  <TableCell>
                    {pkg.AssignedCustomers > 0 ? (
                      <Chip
                        label={`${pkg.AssignedCustomers} customer${pkg.AssignedCustomers > 1 ? 's' : ''}`}
                        color="primary"
                        size="small"
                      />
                    ) : (
                      <Chip label="Not in use" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={pkg.Active ? 'Active' : 'Inactive'}
                      color={pkg.Active ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog(pkg)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color={pkg.Active ? 'warning' : 'success'}
                      onClick={() => handleToggleStatus(pkg)}
                      size="small"
                    >
                      <PowerIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Package Form Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {editingPackage ? 'Edit Package' : 'Create New Package'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Package Name"
                value={formData.PackageName || ''}
                onChange={(e) => handleInputChange('PackageName', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Monthly Price"
                type="number"
                value={formData.MonthlyPrice || ''}
                onChange={(e) => handleInputChange('MonthlyPrice', e.target.value ? parseFloat(e.target.value) : '')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{
                  style: { MozAppearance: 'textfield' },
                  sx: {
                    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  },
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Setup Fee"
                type="number"
                value={formData.SetupFee || ''}
                onChange={(e) => handleInputChange('SetupFee', e.target.value ? parseFloat(e.target.value) : '')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{
                  style: { MozAppearance: 'textfield' },
                  sx: {
                    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.LiveOnSite || false}
                    onChange={(e) => handleInputChange('LiveOnSite', e.target.checked)}
                  />
                }
                label="Show on Website"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.Description || ''}
                onChange={(e) => handleInputChange('Description', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Extensions
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Included Extensions"
                type="number"
                value={formData.IncludedExtensions || ''}
                onChange={(e) => handleInputChange('IncludedExtensions', e.target.value ? parseInt(e.target.value) : '')}
                inputProps={numberInputProps}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Additional Extension Rate"
                type="number"
                value={formData.AdditionalExtenRate || ''}
                onChange={(e) => handleInputChange('AdditionalExtenRate', e.target.value ? parseFloat(e.target.value) : '')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={numberInputProps}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Maximum Extensions"
                type="number"
                value={formData.MaxExtensions || ''}
                onChange={(e) => handleInputChange('MaxExtensions', e.target.value ? parseInt(e.target.value) : null)}
                helperText="Leave blank for unlimited"
                inputProps={numberInputProps}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                DIDs (Phone Numbers)
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Included DIDs"
                type="number"
                value={formData.IncludedDIDs || ''}
                onChange={(e) => handleInputChange('IncludedDIDs', e.target.value ? parseInt(e.target.value) : '')}
                inputProps={numberInputProps}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Additional DID Rate"
                type="number"
                value={formData.AdditionalDIDRate || ''}
                onChange={(e) => handleInputChange('AdditionalDIDRate', e.target.value ? parseFloat(e.target.value) : '')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={numberInputProps}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Maximum DIDs"
                type="number"
                value={formData.MaxDIDs || ''}
                onChange={(e) => handleInputChange('MaxDIDs', e.target.value ? parseInt(e.target.value) : null)}
                helperText="Leave blank for unlimited"
                inputProps={numberInputProps}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Call Minutes
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Included Minutes"
                type="number"
                value={formData.IncludedMinutes || ''}
                onChange={(e) => handleInputChange('IncludedMinutes', e.target.value ? parseInt(e.target.value) : '')}
                InputProps={{
                  endAdornment: <InputAdornment position="end">minutes</InputAdornment>,
                }}
                inputProps={numberInputProps}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Extra Minute Rate"
                type="number"
                value={formData.ExtraMinuteRate || ''}
                onChange={(e) => handleInputChange('ExtraMinuteRate', e.target.value ? parseFloat(e.target.value) : '')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  endAdornment: <InputAdornment position="end">/minute</InputAdornment>,
                }}
                inputProps={{ ...numberInputProps, step: 0.0001 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                SMS/MMS Messaging
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.IncludesSMS || false}
                    onChange={(e) => handleInputChange('IncludesSMS', e.target.checked)}
                  />
                }
                label="Enable SMS/MMS"
              />
            </Grid>

            {formData.IncludesSMS && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Included SMS Messages"
                    type="number"
                    value={formData.sms_included || ''}
                    onChange={(e) => handleInputChange('sms_included', e.target.value ? parseInt(e.target.value) : '')}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">messages/month</InputAdornment>,
                    }}
                    inputProps={numberInputProps}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="SMS Rate (per message over included)"
                    type="number"
                    value={formData.sms_rate || ''}
                    onChange={(e) => handleInputChange('sms_rate', e.target.value ? parseFloat(e.target.value) : '')}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    inputProps={{ ...numberInputProps, step: 0.0001 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Included MMS Messages"
                    type="number"
                    value={formData.mms_included || ''}
                    onChange={(e) => handleInputChange('mms_included', e.target.value ? parseInt(e.target.value) : '')}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">messages/month</InputAdornment>,
                    }}
                    inputProps={numberInputProps}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="MMS Rate (per message over included)"
                    type="number"
                    value={formData.mms_rate || ''}
                    onChange={(e) => handleInputChange('mms_rate', e.target.value ? parseFloat(e.target.value) : '')}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    inputProps={{ ...numberInputProps, step: 0.0001 }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Call Recording
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.IncludesRecording || false}
                    onChange={(e) => handleInputChange('IncludesRecording', e.target.checked)}
                  />
                }
                label="Enable Call Recording"
              />
            </Grid>
            
            {formData.IncludesRecording && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Max Recording Storage (GB)"
                    type="number"
                    value={formData.MaxRecordingStorage || ''}
                    onChange={(e) => handleInputChange('MaxRecordingStorage', e.target.value ? parseInt(e.target.value) : '')}
                    inputProps={numberInputProps}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Storage Rate (per GB over limit)"
                    type="number"
                    value={formData.RecStorageRate || ''}
                    onChange={(e) => handleInputChange('RecStorageRate', e.target.value ? parseFloat(e.target.value) : '')}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      endAdornment: <InputAdornment position="end">/GB</InputAdornment>,
                    }}
                    inputProps={numberInputProps}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Softphone Settings
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.Softphoneenabled || false}
                    onChange={(e) => handleInputChange('Softphoneenabled', e.target.checked)}
                  />
                }
                label="Enable Softphone"
              />
            </Grid>

            {formData.Softphoneenabled && (
              <>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.Softphoneincluded || false}
                        onChange={(e) => handleInputChange('Softphoneincluded', e.target.checked)}
                      />
                    }
                    label="Include Softphones in Base Price (No additional charge)"
                  />
                </Grid>
                {!formData.Softphoneincluded && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Softphone Rate (per user per month)"
                      type="number"
                      value={formData.SoftphoneRate || ''}
                      onChange={(e) => handleInputChange('SoftphoneRate', e.target.value ? parseFloat(e.target.value) : '')}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={numberInputProps}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Softphone Provisioning Options
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.SoftphoneUsersCanEnable || false}
                        onChange={(e) => handleInputChange('SoftphoneUsersCanEnable', e.target.checked)}
                      />
                    }
                    label="Users Can Self-Enable"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.SoftphoneCreateOnPWchange || false}
                        onChange={(e) => handleInputChange('SoftphoneCreateOnPWchange', e.target.checked)}
                      />
                    }
                    label="Auto-Create on Password Change"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.SoftphoneCreateWithExten || false}
                        onChange={(e) => handleInputChange('SoftphoneCreateWithExten', e.target.checked)}
                      />
                    }
                    label="Auto-Create with New Extension"
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Promotional Pricing
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Promotional Price"
                type="number"
                value={formData.promo_price || ''}
                onChange={(e) => handleInputChange('promo_price', e.target.value ? parseFloat(e.target.value) : '')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={numberInputProps}
                helperText="Leave blank for no promotion"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Promotion End Date"
                type="date"
                value={formData.promo_end_date || ''}
                onChange={(e) => handleInputChange('promo_end_date', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Leave blank for no end date"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Display Settings
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Display Order"
                type="number"
                value={formData.display_order || ''}
                onChange={(e) => handleInputChange('display_order', e.target.value ? parseInt(e.target.value) : '')}
                inputProps={numberInputProps}
                helperText="Lower numbers appear first"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_recommended || false}
                    onChange={(e) => handleInputChange('is_recommended', e.target.checked)}
                  />
                }
                label="Mark as Recommended"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Custom Features (Marketing Description)"
                multiline
                rows={3}
                value={formData.custom_features || ''}
                onChange={(e) => handleInputChange('custom_features', e.target.value)}
                helperText="Additional features to display on website"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingPackage ? 'Update Package' : 'Create Package'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServicePackages;